/**
 * Created by andreaskarantzas on 05.05.20.
 */
import { makeAutoObservable } from 'mobx';
import { v4 as UUID } from 'uuid';
import { Media, createMedia } from '../../Media/Media';
import { NativeFeedComponentProps, createNativeFeedComponentProps } from './NativeFeedComponentProps';
import { LocalizedValue, createLocalizedValue } from '../../LocalizedValue';
import { languagePriority } from '../../LocalizedEntity';

export type NativeFeedComponentType =
  | 'View'
  | 'Image'
  | 'Video'
  | 'Heading'
  | 'ItalicHeading'
  | 'Paragraph'
  | 'ItalicParagraph'
  | 'Caption'
  | 'Summary'
  | 'InnerBoldText'
  | 'InnerLinkText'
  | 'BulletPoint'
  | 'NumberPoint'
  | 'Button'
  | 'SecondaryButton';

export type NativeFeedComponent = {
  key: string;
  type: NativeFeedComponentType;
  props: NativeFeedComponentProps;
  children: NativeFeedComponent[];
  text: LocalizedValue[];
  media?: Media;
  /** Per-language image overrides authored in admin-web; `media` stays the default. */
  localizedMedia: { lang: string; media: Media }[];
  readonly defaultText: string;
  getText(lang: string): string;
  getMedia(lang: string): Media | undefined;
};

export function createNativeFeedComponent(json?: Partial<NativeFeedComponent>): NativeFeedComponent {
  return makeAutoObservable({
    key: (json as any)?.key || UUID(),
    type: (json as any)?.type || 'View',
    props: createNativeFeedComponentProps((json as any)?.props || {}),
    children: ((json as any)?.children || []).map((c: any) => createNativeFeedComponent(c)),
    text: ((json as any)?.text || []).map((c: any) => createLocalizedValue(c)),
    media: (json as any)?.media ? createMedia((json as any).media) : undefined,
    localizedMedia: ((json?.localizedMedia ?? []) as { lang?: string; media?: Media }[]).flatMap((m) =>
      m?.lang && m?.media ? [{ lang: m.lang.toLowerCase(), media: createMedia(m.media) }] : [],
    ),

    getText(lang: string): string {
      return this.text.find((n) => n.lang === lang.toLowerCase())?.value ?? this.defaultText;
    },

    /** Media shown for `lang`: its own entry first, then the shared default, then priority fallback. */
    getMedia(lang: string): Media | undefined {
      const exact = this.localizedMedia.find((m) => m.lang === lang.toLowerCase());
      if (exact) {
        return exact.media;
      }
      if (this.media) {
        return this.media;
      }
      for (const l of languagePriority) {
        const entry = this.localizedMedia.find((m) => m.lang === l);
        if (entry) {
          return entry.media;
        }
      }
      return this.localizedMedia.length > 0 ? this.localizedMedia[0].media : undefined;
    },

    get defaultText(): string {
      for (const lang of languagePriority) {
        const entry = this.text.find((l) => l.lang === lang);
        if (entry) {
          return entry.value ?? '';
        }
      }
      const first = this.text[0];
      return first?.value ?? '';
    },
  });
}
